
import { useState, useEffect } from 'react';
import { useFormReset } from './campaign/useFormReset';
import { usePaymentFlow } from './campaign/usePaymentFlow';
import { useCampaignCreation } from './campaign/useCampaignCreation';
import { useCampaignCreator } from './campaign/useCampaignCreator';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';
import { usePaymentSubmission } from './campaign/usePaymentSubmission';

export const useCampaignSubmission = () => {
  // Get form reset functionality
  const { formKey, resetForm } = useFormReset();
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { createCampaign } = useCampaignCreator();
  
  // Get payment flow functionality
  const { 
    showPayment, 
    setShowPayment, 
    currentCampaignId, 
    setCurrentCampaignId, 
    showThankYou,
    setShowThankYou,
    canceledPayment,
    setCanceledPayment
  } = usePaymentFlow(resetForm);
  
  // Get campaign creation functionality - pass setShowPayment and setCurrentCampaignId
  const { 
    isLoading: isTermsSubmitting, 
    showTerms, 
    setShowTerms, 
    handleAcceptTerms 
  } = useCampaignCreation(setShowPayment, setCurrentCampaignId);
  
  // Get payment submission functionality
  const {
    isCreatingCampaign: isSubmittingPayment,
    hasSubmitted: hasSubmittedPayment,
    handlePaymentComplete
  } = usePaymentSubmission(
    setShowPayment,
    setShowThankYou,
    currentCampaignId,
    setCurrentCampaignId
  );
  
  // Add a global event listener for submit button clicks
  useEffect(() => {
    const handleGlobalSubmit = (event: Event) => {
      console.log("useCampaignSubmission: Global submit button click detected", 
        (event as CustomEvent).detail);
      
      // If not already in submission process, go directly to payment (terms handled there)
      if (!isTermsSubmitting && !isSubmittingPayment && !isCreatingCampaign) {
        console.log("useCampaignSubmission: Opening payment popup directly (terms handled within)");
        setShowPayment(true);
      } else {
        console.log("useCampaignSubmission: Ignoring global click - submission already in progress");
      }
    };
    
    // Listen for the custom global submit event
    document.addEventListener('global-submit-button-click', handleGlobalSubmit);
    document.addEventListener('campaign-submit-clicked', handleGlobalSubmit);
    document.addEventListener('payment-form-submit', handleGlobalSubmit);
    
    return () => {
      document.removeEventListener('global-submit-button-click', handleGlobalSubmit);
      document.removeEventListener('campaign-submit-clicked', handleGlobalSubmit);
      document.removeEventListener('payment-form-submit', handleGlobalSubmit);
    };
  }, [isCreatingCampaign, isSubmittingPayment, isTermsSubmitting, setShowTerms]);

  // Check for payment cancellation and preserve step data
  useEffect(() => {
    // Upon component mount, check for payment cancellation
    const paymentCanceled = localStorage.getItem('paymentCanceled');
    const preserveCampaignId = localStorage.getItem('preserveCampaignId');
    
    if (paymentCanceled === 'true' && preserveCampaignId === 'true') {
      console.log("useCampaignSubmission: Detected previous payment cancellation, preserving state");
      setCanceledPayment(true);
    }
    
    // Listen for cancellation events during component lifecycle
    const handleCancellation = () => {
      console.log("useCampaignSubmission: Payment cancellation event detected");
      setCanceledPayment(true);
    };
    
    document.addEventListener('payment-cancel', handleCancellation);
    document.addEventListener('payment-cancel-confirmed', handleCancellation);
    document.addEventListener('payment-cancel-start', handleCancellation);
    
    return () => {
      document.removeEventListener('payment-cancel', handleCancellation);
      document.removeEventListener('payment-cancel-confirmed', handleCancellation);
      document.removeEventListener('payment-cancel-start', handleCancellation);
    };
  }, [setCanceledPayment]);

  // Safe wrapper for handleAcceptTerms
  const safeHandleAcceptTerms = async () => {
    try {
      console.log("useCampaignSubmission: Safely handling accept terms");
      await handleAcceptTerms();
    } catch (error) {
      console.error("Error in useCampaignSubmission.safeHandleAcceptTerms:", error);
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  // Enhanced payment cancellation handler with reliable state preservation
  const handlePaymentCancel = () => {
    console.log("useCampaignSubmission: Payment cancellation - preserving all state and campaign data");
    
    // Flag that we're cancelling (not completing) the payment flow
    setCanceledPayment(true);
    
    // Store all the critical form data we want to preserve
    const formDataFields = [
      'currentStep', 'formData', 'selectedLocations', 'duration', 'devices', 
      'creatives', 'creativeUrls', 'locationCreatives', 'mediaOwnerCreatives', 'campaignName',
      'useLocationSpecificCreatives', 'useMediaOwnerSpecificCreatives',
      'allCreativeUrls'
    ];
    
    // Create a data object with all our preserved values
    const preservedData: Record<string, string | null> = {};
    formDataFields.forEach(key => {
      const value = localStorage.getItem(key);
      preservedData[key] = value;
      
      // Also create a backup version
      if (value && value !== 'null' && value !== 'undefined') {
        localStorage.setItem(`${key}Backup`, value);
      }
    });
    
    // Set payment cancellation flags
    localStorage.setItem('paymentCanceled', 'true');
    localStorage.setItem('paymentCanceledAt', Date.now().toString());
    
    // Close payment dialog
    setShowPayment(false);
    
    // Mark to preserve current step
    const currentStep = localStorage.getItem('currentStep');
    localStorage.setItem('preservedStep', currentStep || '4');
    localStorage.setItem('preserveCampaignId', 'true');
    
    // Dispatch the cancellation event with all preserved data
    document.dispatchEvent(new CustomEvent('payment-cancel', { 
      detail: {
        preserveData: true,
        step: currentStep,
        formData: preservedData,
        timestamp: Date.now()
      }
    }));
    
    // Multiple restoration passes to ensure data consistency
    // First immediate pass
    Object.entries(preservedData).forEach(([key, value]) => {
      if (value && value !== 'null' && value !== 'undefined') {
        localStorage.setItem(key, value);
      }
    });
    
    // Second pass after a short delay
    setTimeout(() => {
      // Force current step restoration
      if (currentStep) {
        localStorage.setItem('currentStep', currentStep);
      }
      
      // Re-apply all data
      Object.entries(preservedData).forEach(([key, value]) => {
        if (value && value !== 'null' && value !== 'undefined') {
          localStorage.setItem(key, value);
        }
      });
      
      console.log("Payment cancel: Re-applied all campaign data to localStorage", preservedData);
      
      // Final pass after UI has updated
      setTimeout(() => {
        // Force current step one more time
        if (currentStep) {
          localStorage.setItem('currentStep', currentStep);
          localStorage.setItem('preservedStep', currentStep);
        }
      }, 500);
    }, 200);
  };

  return {
    isSubmitting: isTermsSubmitting || isSubmittingPayment || isCreatingCampaign,
    showTerms,
    setShowTerms,
    showThankYou,
    setShowThankYou,
    formKey,
    showPayment,
    currentCampaignId,
    handleAcceptTerms: safeHandleAcceptTerms,
    handlePaymentComplete,
    handlePaymentCancel,
    resetForm,
    setShowPayment,
    hasSubmitted: hasSubmitted || hasSubmittedPayment
  };
};
